import { buildUrl } from "@planningcenter/cc-url"
import { useCallback, useEffect, useRef, useState } from "react"

import { useHandleMessageFromChildFrame } from "./useHandleMessageFromChildFrame"

// TLDR; This hook is used to sync the fragment route in the browser's url without causing an annoying flicker.

// It has a couple of interrelated responsibilities whose relationship may not be obvious:

// First:
// It listens for messages from the child frame that indicate that the frame has navigated to a new path.
// When this happens, it updates the fragment route in the browser's url.
// We also track the internal path of the child frame in a ref so that we can compare it to the fragment route in the browser's url.

// Second:
// It compares the current fragment route against the internal path of the child frame.
// The only time that these would ever diverge is if the user manually changes the fragment route in the browser's url.
// When they are not the same, we update the src attribute of the iframe to match the fragment route in the browser's url.

// Why not just update the src attribute of the iframe every time the fragment route changes?
// Because, changing an iframe's src attribute, it will reload the iframe and cause a very annoying flicker.
// It's not a rerender bug. It's a browser behavior that we can't control.

export function useStableIframeSrcAttributeAndSyncFragmentRoute({
  route,
  onInternalPathChange,
}) {
  const [iframeSrcPath, setIframeSrcPath] = useState(route)
  const internalPathOfChatRef = useRef(route)
  const chatBaseURL = buildUrl("/chat")
  const iframeSrc = [chatBaseURL, iframeSrcPath].filter(Boolean).join("/")

  // Keep route & internalPathOfChatRef in sync with the iframe's internal path/query.
  const updateFragmentRouteAndInternalPath = useCallback(
    (event) => {
      const [location, query] = event.data.payload.split("?")
      const path = location.replace(/^\/?chat/, "").replace(/^\/+/, "") || "/"
      const params = new URLSearchParams(query)
      const fullPath = [path, params.toString()].filter(Boolean).join("?")

      internalPathOfChatRef.current = fullPath
      onInternalPathChange(path, params)
    },
    [onInternalPathChange],
  )

  useHandleMessageFromChildFrame(updateFragmentRouteAndInternalPath, {
    allowedOrigin: chatBaseURL,
    messageType: "NEW_ROUTE_RENDERED",
  })

  // Update the iframe's src when is is no longer in sync with the fragment route.
  // This should only happen if the user manually updates the fragment route in the browser URL bar.
  useEffect(() => {
    if (route !== internalPathOfChatRef.current) {
      setIframeSrcPath(route)
    }
  }, [route])

  return iframeSrc
}
